import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { RefreshCw, Search, AlertTriangle, Wrench, X, Layers, Clock, CheckCircle2, Filter, ChevronDown, ChevronRight, FileDown, Truck, ExternalLink, Package } from 'lucide-react';
import ClassificarQuantidadesModal from './ClassificarQuantidadesModal';
import CriarLoteArranjoModal from './CriarLoteArranjoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_FILTROS = [
  { value: 'todos', label: 'Todos' },
  { value: 'pendente', label: 'Pendentes' },
  { value: 'parcial', label: 'Parciais' },
  { value: 'classificada', label: 'Classificadas' },
];

// Estados das OFs — usado para filtro e badge no banner
const OF_ESTADOS = [
  { value: 'rascunho',          label: 'Rascunho',         badge: 'bg-stone-100 text-stone-700 border-stone-200' },
  { value: 'pendente',          label: 'Ready to Start',   badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'aguardar_entrada',  label: 'Aguardar Entrada', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'em_curso',          label: 'Em Curso',         badge: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'pausado',           label: 'Pausado',          badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'terminado',         label: 'Terminado',        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'cancelado',         label: 'Cancelado',        badge: 'bg-red-50 text-red-700 border-red-200' },
];
const OF_ESTADO_MAP = Object.fromEntries(OF_ESTADOS.map(e => [e.value, e]));

/**
 * Estado de classificação de uma anomalia (badge)
 */
function ClassificacaoBadge({ row }) {
  const total = row.quantidade || 0;
  const arr = row.qtd_arranjo || 0;
  const def = row.qtd_defeito || 0;
  const rec = row.qtd_recuperada || 0;
  const pend = row.qtd_pendente || 0;
  const terc = row.qtd_terceirizada || 0;
  const emLote = row.qtd_em_lote_arranjo || 0;
  const lotesArr = row.lotes_arranjo || [];
  const primeiroLote = lotesArr[0]?.codigo;

  if (pend === total) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200" data-testid="badge-classificacao">
        <AlertTriangle className="h-3 w-3" />
        Pendente
      </span>
    );
  }
  if (pend === 0) {
    const tipos = [];
    if (arr > 0) tipos.push(`${arr}A`);
    if (def > 0) tipos.push(`${def}D`);
    if (rec > 0) tipos.push(`${rec}R`);
    if (tipos.length > 1) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200" data-testid="badge-classificacao">
          <CheckCircle2 className="h-3 w-3" />
          Mista ({tipos.join('/')})
        </span>
      );
    }
    if (arr > 0) {
      // Se TODA a quantidade arr foi terceirizada, mostrar Terceirizado
      if (terc >= arr) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300" data-testid="badge-classificacao">
            <Truck className="h-3 w-3" />
            Terceirizado
          </span>
        );
      }
      // Se TODA a quantidade arr está alocada a lote(s) ARR, mostrar Em Lote
      if (emLote >= arr) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300" data-testid="badge-classificacao">
            <Package className="h-3 w-3" />
            Em Lote{primeiroLote ? ` ${primeiroLote}` : ''}{lotesArr.length > 1 ? ` +${lotesArr.length - 1}` : ''}
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200" data-testid="badge-classificacao">
          <Wrench className="h-3 w-3" />
          Arranjo{terc > 0 ? ` (${terc} terc.)` : (emLote > 0 ? ` (${emLote} em lote)` : '')}
        </span>
      );
    }
    if (def > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200" data-testid="badge-classificacao">
          <X className="h-3 w-3" />
          Defeito
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200" data-testid="badge-classificacao">
        <CheckCircle2 className="h-3 w-3" />
        Recuperada
      </span>
    );
  }
  // parcial
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200" data-testid="badge-classificacao">
      <Clock className="h-3 w-3" />
      Parcial ({total - pend}/{total})
    </span>
  );
}

/**
 * Grelha plana (tabela) de todas as anomalias com classificação em quantidade.
 * Agrupada por OF com cabeçalho colapsável.
 */
export default function AnomaliasGrelha({ filtroFixo = null }) {
  const [rows, setRows] = useState([]);
  const [resumo, setResumo] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [operadorFiltro, setOperadorFiltro] = useState('todos');
  const [ofFiltro, setOfFiltro] = useState('todos');
  const [ofEstadoFiltro, setOfEstadoFiltro] = useState('todos');

  // Estado de grupos colapsados — todos colapsados por defeito
  const [colapsados, setColapsados] = useState(new Set());
  const [colapsadosInicializado, setColapsadosInicializado] = useState(false);

  // Modal classificação
  const [classifyRow, setClassifyRow] = useState(null);
  const [criarLoteArrOfId, setCriarLoteArrOfId] = useState(null); // OF para o qual abrir o modal de Criar Lote ARR

  const loadGrelha = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFiltro !== 'todos') params.status_classificacao = statusFiltro;
      const res = await axios.get(`${API}/anomalias-producao/grelha`, { params });
      setRows(res.data?.rows || []);
      setResumo(res.data?.resumo || {});
    } catch (err) {
      toast.error('Erro ao carregar anomalias');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFiltro]);

  useEffect(() => { loadGrelha(); }, [loadGrelha]);

  const operadoresUnicos = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.operador_id && r.operador_nome) m.set(r.operador_id, r.operador_nome); });
    return [...m.entries()].map(([id, nome]) => ({ id, nome }));
  }, [rows]);

  const ofsUnicas = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.of_id && r.of_numero) m.set(r.of_id, r.of_numero); });
    return [...m.entries()].map(([id, numero]) => ({ id, numero }));
  }, [rows]);

  const rowsFiltradas = useMemo(() => {
    return rows.filter(r => {
      // Filtro fixo por aba (Arranjos / Defeitos)
      if (filtroFixo === 'arranjos' && (r.qtd_arranjo || 0) === 0) return false;
      if (filtroFixo === 'defeitos' && (r.qtd_defeito || 0) === 0) return false;
      if (operadorFiltro !== 'todos' && r.operador_id !== operadorFiltro) return false;
      if (ofFiltro !== 'todos' && r.of_id !== ofFiltro) return false;
      if (ofEstadoFiltro !== 'todos' && (r.of_status || '') !== ofEstadoFiltro) return false;
      if (search) {
        const q = search.toLowerCase();
        const t = `${r.of_numero} ${r.lote_codigo} ${r.operacao_codigo} ${r.operacao_nome} ${r.operador_nome} ${r.observacoes || ''}`.toLowerCase();
        if (!t.includes(q)) return false;
      }
      return true;
    });
  }, [rows, operadorFiltro, ofFiltro, ofEstadoFiltro, search, filtroFixo]);

  // Agrupar por OF
  const gruposOF = useMemo(() => {
    const grupos = new Map();
    rowsFiltradas.forEach(r => {
      const key = r.of_id || 'sem-of';
      if (!grupos.has(key)) {
        grupos.set(key, {
          of_id: r.of_id,
          of_numero: r.of_numero || '— sem OF —',
          of_cliente: r.of_cliente,
          of_artigo: r.of_artigo,
          of_status: r.of_status || '',
          rows: [],
          totais: { pecas: 0, arranjo: 0, defeito: 0, recuperada: 0, pendente: 0, arr_em_lote: 0, arr_disponivel: 0 },
        });
      }
      const g = grupos.get(key);
      g.rows.push(r);
      g.totais.pecas += r.quantidade || 0;
      // Sub-totais ARR comuns a todas as abas — quantas peças já estão num Lote ARR e
      // quantas ainda estão disponíveis para criar novo lote (arranjo - em_lote - terceirizadas).
      const arr = r.qtd_arranjo || 0;
      const emLote = r.qtd_em_lote_arranjo || 0;
      const terc = r.qtd_terceirizada || 0;
      g.totais.arr_em_lote += emLote;
      g.totais.arr_disponivel += Math.max(0, arr - emLote - terc);
      // Quando a aba é "Arranjos" só somamos quantidades de arranjo;
      // em "Defeitos" só somamos defeitos. Em todas as outras (dashboard) somamos tudo.
      if (filtroFixo === 'arranjos') {
        g.totais.arranjo += r.qtd_arranjo || 0;
        g.totais.pendente += r.qtd_pendente || 0;
      } else if (filtroFixo === 'defeitos') {
        g.totais.defeito += r.qtd_defeito || 0;
        g.totais.pendente += r.qtd_pendente || 0;
      } else {
        g.totais.arranjo += r.qtd_arranjo || 0;
        g.totais.defeito += r.qtd_defeito || 0;
        g.totais.recuperada += r.qtd_recuperada || 0;
        g.totais.pendente += r.qtd_pendente || 0;
      }
    });
    return [...grupos.values()];
  }, [rowsFiltradas, filtroFixo]);

  // Inicializar todos colapsados na primeira carga
  useEffect(() => {
    if (!colapsadosInicializado && gruposOF.length > 0) {
      setColapsados(new Set(gruposOF.map(g => g.of_id || 'sem-of')));
      setColapsadosInicializado(true);
    }
  }, [gruposOF, colapsadosInicializado]);

  const toggleGrupo = (ofId) => {
    setColapsados(prev => {
      const novo = new Set(prev);
      const key = ofId || 'sem-of';
      if (novo.has(key)) novo.delete(key);
      else novo.add(key);
      return novo;
    });
  };
  const expandirTodos = () => setColapsados(new Set());
  const colapsarTodos = () => setColapsados(new Set(gruposOF.map(g => g.of_id || 'sem-of')));

  const handleClassify = async (payload) => {
    if (!classifyRow) return;
    try {
      await axios.patch(
        `${API}/anomalias-producao/${classifyRow.id}/classificar-quantidades`,
        payload
      );
      toast.success(`Classificado: ${payload.qtd_arranjo} arr + ${payload.qtd_defeito} def + ${payload.qtd_recuperada || 0} rec`);
      setClassifyRow(null);
      loadGrelha();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao classificar');
    }
  };

  return (
    <div className="space-y-4" data-testid="anomalias-grelha">
      {/* Barra filtros */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-stone-500" />
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="Pesquisar OF, lote, operação, operador..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="grelha-search"
            />
          </div>
          <select
            value={statusFiltro}
            onChange={e => setStatusFiltro(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-md text-sm bg-white"
            data-testid="grelha-status-filtro"
          >
            {STATUS_FILTROS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={operadorFiltro}
            onChange={e => setOperadorFiltro(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-md text-sm bg-white max-w-[180px]"
            data-testid="grelha-operador-filtro"
          >
            <option value="todos">Todos os operadores</option>
            {operadoresUnicos.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
          </select>
          <select
            value={ofFiltro}
            onChange={e => setOfFiltro(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-md text-sm bg-white max-w-[180px]"
            data-testid="grelha-of-filtro"
          >
            <option value="todos">Todas as OFs</option>
            {ofsUnicas.map(o => <option key={o.id} value={o.id}>{o.numero}</option>)}
          </select>
          <select
            value={ofEstadoFiltro}
            onChange={e => setOfEstadoFiltro(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-md text-sm bg-white max-w-[180px]"
            data-testid="grelha-of-estado-filtro"
            title="Filtrar por estado da OF"
          >
            <option value="todos">Todos os estados OF</option>
            {OF_ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadGrelha} disabled={loading} data-testid="grelha-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {ofFiltro !== 'todos' && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/ofs/${ofFiltro}/relatorio-pdf`);
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `anomalias_OF.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (_) { toast.error('Erro ao gerar PDF'); }
              }}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="btn-exportar-anomalias-pdf"
              title="Exportar PDF com as anomalias desta OF"
            >
              <FileDown className="h-4 w-4 mr-1" />
              PDF OF
            </Button>
          )}
          <div className="flex items-center gap-1 border-l border-stone-200 pl-2 ml-1">
            <Button variant="ghost" size="sm" onClick={expandirTodos} className="text-xs h-8 text-stone-600" data-testid="grelha-expandir-todos">
              <ChevronDown className="h-3.5 w-3.5 mr-1" /> Expandir
            </Button>
            <Button variant="ghost" size="sm" onClick={colapsarTodos} className="text-xs h-8 text-stone-600" data-testid="grelha-colapsar-todos">
              <ChevronRight className="h-3.5 w-3.5 mr-1" /> Colapsar
            </Button>
          </div>
        </div>
      </Card>

      {/* Lista agrupada por OF */}
      {loading && rows.length === 0 && (
        <Card className="p-8 text-center text-stone-400">A carregar...</Card>
      )}
      {!loading && rowsFiltradas.length === 0 && (
        <Card className="p-8 text-center text-stone-400">Sem anomalias para os filtros aplicados.</Card>
      )}
      {gruposOF.map((g) => {
        const grupoKey = g.of_id || 'sem-of';
        const isCollapsed = colapsados.has(grupoKey);
        // Determinar cor dominante do grupo
        const totalClassificado = g.totais.arranjo + g.totais.defeito + g.totais.recuperada;
        const isUrgente = g.totais.pendente > 0;
        const isCompleto = !isUrgente && totalClassificado > 0;
        const headerBg = isUrgente
          ? 'bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 hover:from-yellow-100 hover:via-amber-100 hover:to-yellow-100 border-l-[6px] border-l-yellow-400'
          : isCompleto
          ? 'bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 hover:from-emerald-100 hover:to-emerald-100 border-l-[6px] border-l-emerald-400'
          : 'bg-stone-50 hover:bg-stone-100 border-l-[6px] border-l-stone-300';
        return (
          <Card key={grupoKey} className="overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`grupo-of-${g.of_id || 'sem'}`}>
            {/* Cabeçalho do grupo (colapsável) */}
            <button
              type="button"
              onClick={() => toggleGrupo(g.of_id)}
              className={`w-full px-4 py-3 flex items-center justify-between border-b border-stone-200 transition ${headerBg}`}
              data-testid={`grupo-toggle-${g.of_id || 'sem'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isCollapsed ? <ChevronRight className="h-5 w-5 text-stone-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-stone-500 flex-shrink-0" />}
                <div className="flex flex-col items-start min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-base text-stone-800 truncate">{g.of_numero}</span>
                    {g.of_status && OF_ESTADO_MAP[g.of_status] && (
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${OF_ESTADO_MAP[g.of_status].badge}`}
                        title="Estado da Ordem de Fabrico"
                        data-testid={`grupo-of-estado-${g.of_id}`}
                      >
                        {OF_ESTADO_MAP[g.of_status].label}
                      </span>
                    )}
                    {g.of_cliente && <span className="text-xs px-2 py-0.5 rounded bg-white/70 text-stone-600 border border-stone-200">{g.of_cliente}</span>}
                    {g.of_artigo && <span className="text-xs text-stone-500 truncate">{g.of_artigo}</span>}
                  </div>
                  <span className="text-[11px] text-stone-500 mt-0.5">
                    {g.rows.length} {g.rows.length === 1 ? 'anomalia' : 'anomalias'} · {g.totais.pecas} {g.totais.pecas === 1 ? 'peça' : 'peças'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Botão de Relatório PDF da OF — só ícone, à esquerda de todos os outros */}
                {g.of_id && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await fetch(`${API}/ofs/${g.of_id}/relatorio-pdf`);
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `relatorio_${g.of_numero || g.of_id}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        toast.error('Erro a gerar relatório: ' + (err.message || ''));
                      }
                    }}
                    className="p-1.5 rounded-md text-stone-500 hover:text-blue-700 hover:bg-blue-50 transition"
                    title="Gerar PDF detalhado da OF"
                    data-testid={`btn-relatorio-of-${g.of_id}`}
                  >
                    <FileDown className="h-4 w-4" />
                  </button>
                )}
                {/* Mini cards numéricos visuais */}
                {g.totais.arranjo > 0 && (
                  <div
                    className="flex items-stretch rounded-lg overflow-hidden border border-amber-300 shadow-sm"
                    title={`${g.totais.arranjo} peça(s) classificadas como Arranjo · ${g.totais.arr_em_lote} já em lote ARR · ${g.totais.arr_disponivel} disponíveis para criar novo lote`}
                  >
                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100">
                      <Wrench className="h-3 w-3 text-amber-700" />
                      <span className="text-xs font-bold text-amber-800">{g.totais.arranjo}</span>
                      <span className="text-[10px] text-amber-700 uppercase">arr</span>
                    </div>
                    {(g.totais.arr_em_lote > 0 || g.totais.arr_disponivel > 0) && (
                      <>
                        <div className="flex items-center gap-1 px-1.5 py-1 bg-rose-50 border-l border-amber-300" title={`${g.totais.arr_em_lote} peças já alocadas a Lote(s) ARR`}>
                          <Package className="h-3 w-3 text-rose-700" />
                          <span className="text-xs font-bold text-rose-800">{g.totais.arr_em_lote}</span>
                          <span className="text-[10px] text-rose-600 lowercase">em lote</span>
                        </div>
                        {g.totais.arr_disponivel > 0 && g.of_id && g.of_numero && g.of_numero !== '— sem OF —' ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCriarLoteArrOfId(g.of_id); }}
                            className="flex items-center gap-1 px-1.5 py-1 border-l border-amber-300 bg-emerald-50 hover:bg-emerald-100 transition cursor-pointer group"
                            title={`Criar novo Lote ARR com as ${g.totais.arr_disponivel} peças disponíveis →`}
                            data-testid={`btn-criar-lote-arr-${g.of_id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                            <span className="text-xs font-bold text-emerald-800">{g.totais.arr_disponivel}</span>
                            <span className="text-[10px] text-emerald-600 lowercase">disp.</span>
                            <span className="text-[10px] text-emerald-600 ml-0.5 opacity-0 group-hover:opacity-100 transition">+ Lote</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 px-1.5 py-1 border-l border-amber-300 bg-stone-50" title="Sem peças disponíveis (todas alocadas)">
                            <CheckCircle2 className="h-3 w-3 text-stone-400" />
                            <span className="text-xs font-bold text-stone-500">0</span>
                            <span className="text-[10px] text-stone-400 lowercase">disp.</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {g.totais.defeito > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-100 border border-red-300">
                    <X className="h-3 w-3 text-red-700" />
                    <span className="text-xs font-bold text-red-800">{g.totais.defeito}</span>
                    <span className="text-[10px] text-red-700 uppercase">def</span>
                  </div>
                )}
                {g.totais.recuperada > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-100 border border-green-300">
                    <CheckCircle2 className="h-3 w-3 text-green-700" />
                    <span className="text-xs font-bold text-green-800">{g.totais.recuperada}</span>
                    <span className="text-[10px] text-green-700 uppercase">rec</span>
                  </div>
                )}
                {g.totais.pendente > 0 && (
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-yellow-400 text-yellow-950 font-bold animate-pulse-slow shadow-sm">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    <span className="text-xs">{g.totais.pendente}</span>
                    <span className="text-[10px] uppercase">pendentes</span>
                  </div>
                )}
                {/* "Tudo classificado" só faz sentido na vista geral (Dashboard).
                    Nas abas filtradas (Arranjos/Defeitos) a métrica é parcial e induz em erro. */}
                {isCompleto && !filtroFixo && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-800">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-semibold uppercase">Tudo classificado</span>
                  </div>
                )}
                {/* Atalho discreto para lotes ARR — só aparece se há peças classificadas como Arranjo */}
                {g.of_id && g.totais.arranjo > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/gestor/ofs?of=${g.of_id}&tab=lotes`, '_self');
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-stone-500 hover:text-orange-700 hover:bg-orange-50 transition"
                    title="Abrir lotes ARR desta OF"
                    data-testid={`btn-atalho-lotes-arr-${g.of_id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-medium">Lotes ARR</span>
                  </button>
                )}
              </div>
            </button>

            {/* Tabela do grupo */}
            {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid={`grupo-table-${g.of_id || 'sem'}`}>
                  <thead className="bg-white border-b border-stone-100 text-[10px] uppercase text-stone-500">
                    <tr className="text-left">
                      <th className="px-3 py-1.5">Data / Hora</th>
                      <th className="px-3 py-1.5">Lote</th>
                      <th className="px-3 py-1.5">Operação</th>
                      <th className="px-3 py-1.5">Modelo</th>
                      <th className="px-3 py-1.5">Marca</th>
                      <th className="px-3 py-1.5">Operador</th>
                      <th className="px-3 py-1.5 text-center">Fase</th>
                      <th className="px-3 py-1.5 text-center">T.Falta</th>
                      <th className="px-3 py-1.5 text-center" title="Tempo total recuperação (T.Falta × peças por recuperar)">T.Total</th>
                      <th className="px-3 py-1.5 text-right">Total</th>
                      <th className="px-3 py-1.5 text-right text-amber-700">Arr</th>
                      <th className="px-3 py-1.5 text-right text-red-700">Def</th>
                      <th className="px-3 py-1.5 text-right text-green-700">Rec</th>
                      <th className="px-3 py-1.5 text-right text-yellow-700">Pend</th>
                      <th className="px-3 py-1.5">Estado</th>
                      <th className="px-3 py-1.5">Tags</th>
                      <th className="px-3 py-1.5 text-center">Acção</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {g.rows.map(r => {
                      const rast = r.rastreabilidade || {};
                      const fasePct = rast.ops_total ? Math.round((rast.ops_completadas / rast.ops_total) * 100) : 0;
                      const isClassificada = r.qtd_pendente === 0 && r.quantidade > 0;
                      const isParcial = r.qtd_pendente > 0 && (r.qtd_arranjo > 0 || r.qtd_defeito > 0 || r.qtd_recuperada > 0);
                      // Estado da operação
                      let opEstadoBadge = null;
                      const oe = r.operacao_estado;
                      if (oe === 'em_curso' || oe === 'em-curso') {
                        opEstadoBadge = <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[9px] font-bold uppercase" title="Operação em curso">EM CURSO</span>;
                      } else if (oe === 'concluida' || oe === 'concluído' || oe === 'concluido') {
                        opEstadoBadge = <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase" title="Operação concluída">CONCLUÍDA</span>;
                      } else if (oe === 'pausada') {
                        opEstadoBadge = <span className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold uppercase" title="Operação pausada">PAUSADA</span>;
                      }
                      return (
                        <tr key={r.id} className="hover:bg-stone-50" data-testid={`grelha-row-${r.id}`}>
                          {/* Data + Hora numa linha */}
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-stone-700 font-medium">
                              {r.criado_em ? new Date(r.criado_em).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                            </span>
                            <span className="text-stone-400 ml-1">
                              {r.criado_em ? new Date(r.criado_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </td>
                          {/* Lote */}
                          <td className="px-3 py-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-stone-100 text-stone-700 font-mono text-[11px] font-semibold">
                              {r.lote_codigo || '—'}
                            </span>
                          </td>
                          {/* Código + Operação na mesma linha */}
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1.5 max-w-[280px]">
                              <span className="font-mono font-semibold text-primary text-[11px] flex-shrink-0">{r.operacao_codigo}</span>
                              <span className="text-[11px] text-stone-600 truncate">{r.operacao_nome}</span>
                            </div>
                          </td>
                          {/* Modelo */}
                          <td className="px-3 py-1.5 text-stone-700 truncate max-w-[140px]" title={r.of_modelo}>{r.of_modelo || '—'}</td>
                          {/* Marca */}
                          <td className="px-3 py-1.5 text-stone-700 truncate max-w-[120px]" title={r.of_marca}>{r.of_marca || '—'}</td>
                          {/* Operador */}
                          <td className="px-3 py-1.5 text-stone-700">{r.operador_nome || '—'}</td>
                          {/* Fase */}
                          <td className="px-3 py-1.5 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <span className="text-xs font-semibold text-blue-700">{rast.ops_completadas || 0}/{rast.ops_total || 0}</span>
                              <div className="w-12 h-1 bg-stone-200 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500" style={{ width: `${fasePct}%` }} />
                              </div>
                            </div>
                          </td>
                          {/* T.Falta */}
                          <td className="px-3 py-1.5 text-center text-amber-700 whitespace-nowrap">
                            <Clock className="h-3 w-3 inline mr-0.5" />
                            <span className="font-medium">{rast.tempo_restante_min || 0}m</span>
                          </td>
                          {/* T.Total = tempo restante × peças por recuperar */}
                          <td className="px-3 py-1.5 text-center text-purple-700 font-semibold whitespace-nowrap" title="T.Falta × peças por recuperar">
                            {r.tempo_total_recup_min || 0}m
                          </td>
                          {/* Quantidades */}
                          <td className="px-3 py-1.5 text-right font-semibold text-stone-800">{r.quantidade}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">
                            <span className="font-bold text-amber-700">{r.qtd_arranjo}</span>
                          </td>
                          <td className="px-3 py-1.5 text-right font-bold text-red-700">{r.qtd_defeito}</td>
                          <td className="px-3 py-1.5 text-right font-bold text-green-700">{r.qtd_recuperada || 0}</td>
                          <td className="px-3 py-1.5 text-right">
                            {r.qtd_pendente > 0 ? (
                              <span className="px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-bold text-[11px]">{r.qtd_pendente}</span>
                            ) : (
                              <span className="text-stone-300">—</span>
                            )}
                          </td>
                          {/* Estado classificação + estado operação */}
                          <td className="px-3 py-1.5">
                            <div className="flex flex-col gap-0.5 items-start">
                              <ClassificacaoBadge row={r} />
                              {opEstadoBadge}
                            </div>
                          </td>
                          {/* Tags */}
                          <td className="px-3 py-1.5">
                            {(r.tags && r.tags.length > 0) ? (
                              <div className="flex flex-wrap gap-0.5 max-w-[140px]">
                                {r.tags.slice(0, 3).map((t, i) => (
                                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-medium">{t}</span>
                                ))}
                                {r.tags.length > 3 && <span className="text-[10px] text-stone-500">+{r.tags.length - 3}</span>}
                              </div>
                            ) : <span className="text-stone-300 text-[11px]">—</span>}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <Button
                              size="sm"
                              variant={isClassificada ? 'outline' : 'default'}
                              onClick={() => setClassifyRow(r)}
                              className={`h-7 px-2 text-xs ${isClassificada ? '' : isParcial ? 'bg-amber-600 hover:bg-amber-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                              data-testid={`classificar-btn-${r.id}`}
                            >
                              {isClassificada ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Wrench className="h-3 w-3 mr-1" />}
                              {isClassificada ? 'Editar' : isParcial ? 'Continuar' : 'Classificar'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      <ClassificarQuantidadesModal
        open={!!classifyRow}
        onOpenChange={(o) => { if (!o) setClassifyRow(null); }}
        row={classifyRow}
        onConfirm={handleClassify}
      />

      {/* Modal para criar Lote ARR a partir das peças disponíveis de uma OF */}
      <CriarLoteArranjoModal
        open={!!criarLoteArrOfId}
        onOpenChange={(o) => { if (!o) setCriarLoteArrOfId(null); }}
        ofId={criarLoteArrOfId}
        onSuccess={() => { setCriarLoteArrOfId(null); loadGrelha(); }}
      />
    </div>
  );
}
